import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { DateTime as LuxonDateTime } from 'luxon';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';
import { Loading } from 'src/shared/components/loading/loading';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Schedule } from 'src/providers/schedule/schedule';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { Client } from 'src/providers/client/client';
import { Pros } from 'src/providers/pros/pros';
import { BookService } from 'src/shared/services/book.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

@Component({
    templateUrl: 'reschedule-cancel.html',
    standalone: false
})

export class RescheduleCancelPage implements OnInit {

  dialogParams: any;
  booking: any;
  jobId: any;
  addressId: any;
  clientInfo: any;
  accountType: string;

  
  boundRescheduleJob = this.rescheduleJob.bind(this);
  boundSkipJob = this.skipJob.bind(this);

constructor(
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    public windowService: WindowService,
    private modalCtrl: ModalController,
    private schedule: Schedule,
    private guestReservations: GuestReservations,
    private util: Util,
    private storage: TidyStorage,
    private client: Client,
    private pros: Pros,
    private bookService: BookService,
    private demoBlock: DemoBlockService
  ) {}

  async ngOnInit() {
    this.rightSidePanelService.setDialogPageTitle('Reschedule / Skip');
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.booking = await this.getParam('booking');
    this.jobId = await this.getParam('jobId');
    this.addressId = await this.getParam('addressId');
  }

  async getParam(paramName): Promise<any> {
    let param = this.dialogParams?.[paramName];
    if (param === null || param === undefined) {
      param = this.navCtrl.getParam(paramName)
    }
    if (param === null || param === undefined) {
      param = await this.storage.retrieve(paramName);
    }
    return param;
  }

  async getAccountType() {
    this.clientInfo = await this.client.getClientInfo();
    const types = {
      regular: 'Residential',
      realtor: 'Realtor',
      rental: 'Rental',
      company: 'Office',
    };
    return types[this.clientInfo.customer_account.account_type];
  }

  async rescheduleJob() {
    const address = this.booking?.address;
    const bookingType = 'reschedule_job';
    const jobId = this.jobId;
    const bookingId = this.booking?.booking?.id;
    const serviceTypeDetails = this.booking?.service_type_details;
    const planId = this.booking?.booking?.plan_id;
    const categoryId = serviceTypeDetails?.service_category?.id;
    const params = {
      address,
      bookingType,
      jobId,
      bookingId,
      planId,
      categoryId
    }
    const homekeepers = this.booking?.booking?.current_job?.homekeepers;

    if (homekeepers?.length === 1 && homekeepers?.[0]?.id) {
      params['hkId'] = homekeepers[0].id;
      params['filterByPro'] = true;
    }
    params['isRescheduling'] = true;
    const startDateTime = LuxonDateTime.fromISO(this.booking?.booking?.current_job?.start_datetime_local ?? '', { setZone: true });
    params['originalJobData'] = {
      serviceType: serviceTypeDetails?.service_category?.id,
      serviceKey: serviceTypeDetails?.key,
      originalDate: this.booking?.booking?.current_job?.start_datetime_local,
      originalTime: startDateTime.isValid ? startDateTime.toFormat('HH:mm') : undefined,
      originalPro: homekeepers?.[0]?.id,
      originalAddress: address?.id,
      originalBookingId: bookingId,
      originalPlanId: planId,
      originalJobNotes: this.booking?.booking_notes || []
    };

    await this.storage.save('bookJobBackPage', 'job');
    await this.bookService.goToBookJob(params);
  }

  @Loading('', true)
  async skipJob() {
    if (this.demoBlock.block()) {return;}
    try {
      const { reservationId, showAutomaticBookingQuestion, reservationDateTime } = await this.guestReservations.getLastReservationDetails(this.addressId, this.jobId);
      const params: ConfirmPageParamsModel = {
        title: 'Skip Job?',
        body: 'This will cancel the job (does not affect other jobs).',
        backText: 'Go Back',
        confirmText: 'Skip Job',
        confirmAction: this.skipJobAction.bind(this, this.booking?.booking?.id, showAutomaticBookingQuestion, reservationId),
        showAutomaticBookingQuestion,
        reservationDateTime
      }
      const confirmationModal = await this.modalCtrl.create({
        component: ConfirmPage,
        componentProps: params,
        animated: false,
        cssClass: showAutomaticBookingQuestion ? 'tall-confirm-modal' : 'confirm-modal'
      });
      await confirmationModal.present();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async skipJobAction(bookingId, showAutomaticBookingQuestion, reservationId) {
    try {
      if (showAutomaticBookingQuestion) {
        const keepAutomaticBookingOn = localStorage.getItem('keepAutomaticBookingOn') === 'true';
        localStorage.removeItem('keepAutomaticBookingOn');
        if (!keepAutomaticBookingOn) {
          await this.guestReservations.toggleAutomaticBookingForReservation(reservationId, false);
        }
      }
      await this.schedule.cancelJob(bookingId);
      localStorage.setItem('madeUpdateToSchedule', 'true');
      this.util.showSuccess('Job skipped');
      this.modalCtrl.dismiss();
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        const shouldShowAllToDosPage = localStorage.getItem('shouldShowAllToDosPage') === 'true';
        this.navCtrl.navigateForward(shouldShowAllToDosPage ? 'schedule' : 'schedule-list');
      }
    } catch(err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

}